import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import { reportError } from "../../../shared/sentry";
import { createPortal } from "react-dom";

const SharePopup = ({ userName }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const popupRef = useRef(null);

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const shareUrl = useMemo(() => {
    if (!userName) {
      return origin;
    }

    const sanitizedOrigin = origin.endsWith("/")
      ? origin.slice(0, origin.length - 1)
      : origin;

    return `${sanitizedOrigin}/${userName}`;
  }, [origin, userName]);

  const title = useMemo(
    () => `Check out this creator on JuicyHouse: ${userName ?? ""}`,
    [userName]
  );

  const handleCopyLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      reportError(error, 'SharePopup copy link');
    }
  };

  /**
   * Instagram does not provide a web share-URL API. Use Web Share API on
   * mobile (iOS/Android) so Instagram appears in the native share sheet;
   * on desktop fallback: copy link and open instagram.com to paste in DMs.
   * @see https://developers.facebook.com/docs/instagram-platform/sharing-to-stories
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
   */
  const handleInstagramShare = async () => {
    const shareData = { url: shareUrl, title, text: title };
    const canUseWebShare =
      typeof navigator !== "undefined" &&
      navigator.share &&
      (navigator.canShare ? navigator.canShare(shareData) : true);

    try {
      if (canUseWebShare) {
        await navigator.share(shareData);
      } else {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      } catch (fallbackErr) {
        reportError(fallbackErr, 'SharePopup Instagram share fallback');
      }
    }
  };

  useEffect(() => {
    if (!showPopup) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
        document.body.classList.remove('modal-open')
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  return (
    <>
      <button
        type="button"
        className="cm_share_profile"
        onClick={() => (setShowPopup((prev) => !prev), document.body.classList.add('modal-open'))}
        aria-expanded={showPopup}
        aria-label="Share profile"
      >
        <i className="fa-solid fa-share-nodes" />
      </button>

      {showPopup && createPortal(
        <div>
          <div
            ref={popupRef}
            className="cm_share_popup_wraper"
            role="dialog"
            aria-modal="true"
            aria-label="Share this profile"
          >
            <div style={{ gridTemplateColumns: 'repeat(3, 1fr)' }} className="grid gap-3 text-center text-gray-600">
              <TwitterShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-x-twitter"></i>
              </TwitterShareButton>

              <RedditShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-reddit" />
              </RedditShareButton>

              <TelegramShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-telegram" />
              </TelegramShareButton>

              <WhatsappShareButton url={shareUrl} title={title}>
                <i className="fa-brands fa-whatsapp" />
              </WhatsappShareButton>

              <span
                // onClick={() =>
                //   window.open(
                //     `https://www.instagram.com/?url=${encodeURIComponent(shareUrl)}`,
                //     "_blank",
                //     "noopener,noreferrer"
                //   )
                // }
                onClick={handleInstagramShare}
                aria-label="Share on Instagram"
              >
                <i className="fa-brands fa-instagram" />
              </span>

              <span
                onClick={handleCopyLink}
                title="Copy link"
                aria-label="Copy profile link"
              >
                <i className="fa-regular fa-copy" />
              </span>
            </div>

            {copied && (
              <span className="mt-3 block text-center text-sm text-green-500">
                Copied!
              </span>
            )}
          </div>

          <div
            className="cm_share_popup_overlay cm_bg_blur"
            role="presentation"
            onClick={() => setShowPopup(false)}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default SharePopup;
